import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import fetchClient from 'interfaces/fetchClient';
import env from 'variables/config';
import Card from 'components/card';

// ─── Pennylane brand colours ────────────────────────────────────────────────
const PL_GREEN = '#00B04F';
const PL_DARK = '#0A2540';

// ─── Sub-components ──────────────────────────────────────────────────────────

const PennylaneWordmark = () => (
    <svg viewBox="0 0 160 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-7 w-auto">
        <rect width="32" height="32" rx="8" fill={PL_GREEN} />
        <path d="M8 8h10a6 6 0 0 1 0 12H8V8Z" fill="white" />
        <path d="M8 20h7" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
        <text x="40" y="23" fontFamily="Inter, sans-serif" fontSize="16" fontWeight="700" fill={PL_DARK}>
            pennylane
        </text>
    </svg>
);

interface StepBadgeProps {
    index: number;
    label: string;
    description: string;
    status: 'done' | 'active' | 'pending';
}

const StepBadge = ({ index, label, description, status }: StepBadgeProps) => {
    const isDone = status === 'done';
    const isActive = status === 'active';
    return (
        <div className={`flex items-start gap-3 rounded-xl px-4 py-3 transition-all duration-300 ${isActive ? 'bg-green-50 border border-green-200' : isDone ? 'bg-gray-50' : 'bg-white'}`}>
            <div
                className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-all duration-300 ${
                    isDone
                        ? 'bg-green-500 text-white'
                        : isActive
                        ? 'bg-green-100 text-green-700 ring-2 ring-green-400 ring-offset-1'
                        : 'bg-gray-100 text-gray-400'
                }`}
            >
                {isDone ? (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                ) : (
                    index
                )}
            </div>
            <div className="min-w-0 flex-1">
                <p className={`text-sm font-semibold break-words ${isActive ? 'text-green-800' : isDone ? 'text-gray-500' : 'text-gray-700'}`}>
                    {label}
                </p>
                <p className={`text-xs mt-0.5 break-words ${isActive ? 'text-green-600' : 'text-gray-400'}`}>{description}</p>
            </div>
        </div>
    );
};

const RequirementRow = ({
    label,
    value,
    ok,
}: {
    label: string;
    value: string | undefined;
    ok: boolean;
}) => (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
        <span className="text-xs text-gray-500">{label}</span>
        <div className="flex items-center gap-1.5">
            <span className={`text-xs font-medium truncate max-w-[180px] ${ok ? 'text-gray-800' : 'text-red-400 italic'}`}>
                {value || 'Non renseigné'}
            </span>
            {ok ? (
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-green-100">
                    <svg className="h-2.5 w-2.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                </span>
            ) : (
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-red-100">
                    <svg className="h-2.5 w-2.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </span>
            )}
        </div>
    </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const PennylaneOnboarding = () => {
    const [user, setUser] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [authorizeUrl, setAuthorizeUrl] = useState<string | null>(null);
    const [tokenInfo, setTokenInfo] = useState<{ accessTokenExpiresAt: string | null; refreshTokenExpiresAt: string | null } | null>(null);
    const [pdpStatus, setPdpStatus] = useState<{ activated: boolean; activationUrl: string | null } | null>(null);
    const ownerId = localStorage.getItem('ownerId');

    const fetchUser = () => {
        const token = localStorage.getItem('token');
        fetchClient
            .get(`${env.apiUrl}/user/me`, { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => setUser(res.data))
            .catch(console.error);
    };

    const fetchTokenInfo = () => {
        const token = localStorage.getItem('token');
        fetchClient
            .get(`${env.apiUrl}/user/pennylane/info`, { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => setTokenInfo(res.data))
            .catch(() => setTokenInfo(null));
    };

    useEffect(() => {
        fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (user?.pennylaneStatus === 'connected') fetchTokenInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.pennylaneStatus]);

    // Once a Pennylane company exists, check whether it's been accepted as PDP
    useEffect(() => {
        if (!user?.pennylaneCompanyId) return;
        const token = localStorage.getItem('token');
        fetchClient
            .get(`${env.apiUrl}/user/pennylane/pdp-status`, { headers: { Authorization: `Bearer ${token}` } })
            .then((res) => setPdpStatus(res.data))
            .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.pennylaneCompanyId]);

    // If already pending_oauth or account_conflict on load, fetch the authorize URL
    useEffect(() => {
        if (user?.pennylaneStatus === 'pending_oauth' || user?.pennylaneStatus === 'account_conflict') {
            const token = localStorage.getItem('token');
            fetchClient
                .get(`${env.apiUrl}/user/pennylane/authorize-url`, {
                    headers: { Authorization: `Bearer ${token}` },
                })
                .then((res) => setAuthorizeUrl(res.data.authorizeUrl))
                .catch(console.error);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.pennylaneStatus]);

    if (ownerId) return null;

    const pennylaneStatus: string = user?.pennylaneStatus || 'not_connected';
    const isConnected = pennylaneStatus === 'connected';
    const isPendingOAuth = pennylaneStatus === 'pending_oauth';
    const hasError = pennylaneStatus === 'error';
    const hasAccountConflict = pennylaneStatus === 'account_conflict';

    // Vérification des données requises
    const checks = {
        company: !!user?.company,
        siren: !!user?.siren && user.siren.length >= 9,
        email: !!user?.billingEmail || !!user?.email,
        fullName: !!user?.fullName,
        phone: !!user?.phone,
        address: !!user?.billingAddress,
        city: !!user?.city,
        postalCode: !!user?.postalCode,
        activityCode: !!user?.activityCode,
    };
    const allGood = Object.values(checks).every(Boolean);
    const missingCount = Object.values(checks).filter((v) => !v).length;

    const connect = async () => {
        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            const res = await fetchClient.post(
                `${env.apiUrl}/user/pennylane/connect`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setAuthorizeUrl(res.data.authorizeUrl);
            if (res.data.accountConflict) {
                toast.info(res.data.message || 'Un compte Pennylane existe déjà avec votre entreprise, vous allez être redirigé pour vous connecter.');
            } else {
                toast.success('✅ Compte Pennylane créé. Autorisez maintenant l\'accès à Toola.');
            }
            fetchUser();
        } catch (err: any) {
            const msg = err?.response?.data?.message || 'Erreur lors de la connexion à Pennylane';
            toast.error(msg);
            fetchUser();
        } finally {
            setLoading(false);
        }
    };

    const openOAuthWindow = () => {
        if (!authorizeUrl) return;
        window.location.href = authorizeUrl;
    };

    // step 1 → données | step 2 → créer compte | step 3 → autoriser OAuth | step 4 → connecté
    const currentStep: number = isConnected ? 4 : isPendingOAuth ? 3 : allGood ? 2 : 1;

    if (hasAccountConflict) {
        const conflictDate = user?.pennylaneConflictCompanyCreatedAt
            ? new Date(user.pennylaneConflictCompanyCreatedAt)
            : null;
        const conflictDateStr = conflictDate
            ? conflictDate.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
            : null;
        const daysAgo = conflictDate
            ? Math.max(0, Math.floor((Date.now() - conflictDate.getTime()) / (1000 * 60 * 60 * 24)))
            : null;
        const daysAgoLabel =
            daysAgo === null ? '' : daysAgo === 0 ? "aujourd'hui" : daysAgo === 1 ? 'hier' : `il y a ${daysAgo} jours`;

        const openExistingAccountAuth = () => {
            if (!authorizeUrl) return;
            window.open(authorizeUrl, '_blank', 'noopener,noreferrer');
        };

        return (
            <Card extra="w-full mt-3 border overflow-hidden">
                <div
                    className="flex items-center gap-3 px-6 py-3"
                    style={{ background: 'linear-gradient(90deg, #0A2540 0%, #00402A 100%)' }}
                >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-400 text-sm">
                        ⚠
                    </span>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-white">
                            Compte Pennylane déjà existant pour votre entreprise
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5 truncate">
                            Connectez-vous à ce compte pour activer la facturation électronique.
                        </p>
                    </div>
                </div>

                <div className="flex flex-col items-center text-center px-8 py-12">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
                        <svg className="h-9 w-9 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                        </svg>
                    </div>
                    <h3 className="mt-5 text-lg font-bold text-gray-800">
                        Un compte Pennylane existe déjà pour votre entreprise
                    </h3>
                    <p className="mt-3 max-w-md text-sm text-gray-500">
                        {conflictDateStr ? (
                            <>
                                Ce compte a été créé sur Pennylane le <strong className="text-gray-700">{conflictDateStr}</strong>{' '}
                                ({daysAgoLabel}).
                            </>
                        ) : (
                            <>Un compte a déjà été créé pour cette entreprise sur Pennylane.</>
                        )}
                        {' '}Pour continuer, vous devez vous connecter avec ce compte existant plutôt que d'en créer un nouveau.
                    </p>
                    <button
                        onClick={openExistingAccountAuth}
                        disabled={!authorizeUrl}
                        className="mt-6 flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:opacity-90 hover:shadow-md active:scale-95 disabled:opacity-50"
                        style={{ background: `linear-gradient(135deg, ${PL_DARK} 0%, ${PL_GREEN} 100%)` }}
                    >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Connecter Toola avec mon compte Pennylane existant
                    </button>
                    <p className="mt-3 text-xs text-gray-400">
                        Ouvre Pennylane dans un nouvel onglet — connectez-vous avec l'email associé à cette entreprise, puis revenez sur cette page.
                    </p>
                </div>
            </Card>
        );
    }

    return (
        <Card extra="w-full mt-3 border overflow-hidden">
            {/* ── Urgency banner ── */}
            <div
                className="flex items-center gap-3 px-6 py-3"
                style={{ background: 'linear-gradient(90deg, #0A2540 0%, #00402A 100%)' }}
            >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-400 text-sm">
                    ⚡
                </span>
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-white">
                        Facturation électronique obligatoire dès le{' '}
                        <span className="text-amber-300">1er septembre 2026</span>
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">
                        Connectez Pennylane maintenant pour être conforme et envoyer vos factures en un clic.
                    </p>
                </div>
                {pdpStatus?.activated ? (
                    <span className="shrink-0 rounded-full bg-green-500 px-3 py-1 text-xs font-bold text-white">
                        ✓ Conforme
                    </span>
                ) : pdpStatus && pdpStatus.activationUrl ? (
                    <a
                        href={pdpStatus.activationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 flex items-center gap-1 rounded-full bg-amber-400 px-3 py-1 text-xs font-bold text-gray-900 hover:bg-amber-300 transition-colors"
                    >
                        Activer le PDP Pennylane
                    </a>
                ) : null}
            </div>

            {/* ── Card body ── */}
            <div className="px-6 py-5">
                {/* Header row */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <PennylaneWordmark />
                        {isConnected ? (
                            <span className="flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                                <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                                Connecté
                            </span>
                        ) : isPendingOAuth ? (
                            <span className="flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
                                <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
                                En attente d'autorisation
                            </span>
                        ) : hasError ? (
                            <span className="flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-600">
                                Erreur de connexion
                            </span>
                        ) : (
                            <span className="flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-500">
                                Non connecté
                            </span>
                        )}
                    </div>
                    <button
                        onClick={() => setExpanded((v) => !v)}
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        {expanded ? 'Réduire' : 'Voir les détails'}
                        <svg
                            className={`h-3.5 w-3.5 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                </div>

                {/* Tagline */}
                {!isConnected && !isPendingOAuth && (
                    <p className="mt-3 text-sm text-gray-500">
                        Pennylane est le logiciel comptable qui va gérer votre facturation électronique.
                        La connexion prend <strong className="text-gray-700">moins de 30 secondes</strong> et vos données sont pré-remplies.
                    </p>
                )}
                {isPendingOAuth && (
                    <p className="mt-3 text-sm text-amber-700 font-medium">
                        Votre compte Pennylane a été créé. Il reste une dernière étape : <strong>autoriser Toola à accéder à Pennylane</strong> en cliquant sur le bouton ci-dessous.
                    </p>
                )}
                {isConnected && (
                    <p className="mt-3 text-sm text-green-700 font-medium">
                        Votre compte Pennylane est actif. Toutes vos futures factures seront automatiquement transmises conformément à la législation.
                    </p>
                )}

                {/* Steps — 4 étapes. Fixed 2 columns: this card sits in a half-width
                    dashboard column (lg:grid-cols-2 in the settings page), so a
                    viewport-based sm:grid-cols-4 breakpoint would force 4 cramped
                    columns into a container only wide enough for 2. */}
                <div className="mt-4 grid grid-cols-2 gap-2">
                    <StepBadge
                        index={1}
                        label="Données entreprise"
                        description="SIREN, ville, email…"
                        status={currentStep > 1 ? 'done' : currentStep === 1 ? 'active' : 'pending'}
                    />
                    <StepBadge
                        index={2}
                        label="Création du compte"
                        description="Utilisateur & société"
                        status={currentStep > 2 ? 'done' : currentStep === 2 ? 'active' : 'pending'}
                    />
                    <StepBadge
                        index={3}
                        label="Autorisation"
                        description="Donnez l'accès à Toola"
                        status={currentStep > 3 ? 'done' : currentStep === 3 ? 'active' : 'pending'}
                    />
                    <StepBadge
                        index={4}
                        label="Factures électroniques"
                        description="Envoi automatique actif"
                        status={currentStep === 4 ? 'done' : 'pending'}
                    />
                </div>

                {/* Expandable section */}
                {expanded && (
                    <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                        <p className="text-xs font-semibold text-gray-600 mb-2">
                            Données qui seront transmises à Pennylane
                        </p>
                        <RequirementRow label="Raison sociale" value={user?.company} ok={checks.company} />
                        <RequirementRow label="SIREN" value={user?.siren} ok={checks.siren} />
                        <RequirementRow
                            label="Email de facturation"
                            value={user?.billingEmail || user?.email}
                            ok={checks.email}
                        />
                        <RequirementRow label="Votre nom complet" value={user?.fullName} ok={checks.fullName} />
                        <RequirementRow label="Téléphone" value={user?.phone} ok={checks.phone} />
                        <RequirementRow label="Adresse" value={user?.billingAddress} ok={checks.address} />
                        <RequirementRow label="Ville" value={user?.city} ok={checks.city} />
                        <RequirementRow label="Code postal" value={user?.postalCode} ok={checks.postalCode} />
                        <RequirementRow label="Code APE (NAF)" value={user?.activityCode} ok={checks.activityCode} />
                        {!allGood && (
                            <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 flex gap-2">
                                <span className="text-amber-500 text-sm">⚠</span>
                                <p className="text-xs text-amber-700">
                                    <strong>{missingCount} donnée{missingCount > 1 ? 's' : ''} manquante{missingCount > 1 ? 's' : ''}.</strong>{' '}
                                    Complétez la section <em>Votre entreprise</em> ci-dessous avant de connecter.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* ── CTA zone ── */}
                <div className="mt-5">
                    {/* Step 1→2 : créer le compte Pennylane */}
                    {!isConnected && !isPendingOAuth && (
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                            <button
                                onClick={connect}
                                disabled={loading || !allGood}
                                className={`flex items-center gap-2 rounded-xl px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 ${
                                    allGood && !loading
                                        ? 'hover:opacity-90 hover:shadow-md active:scale-95'
                                        : 'opacity-50 cursor-not-allowed'
                                }`}
                                style={{
                                    background:
                                        allGood
                                            ? `linear-gradient(135deg, ${PL_DARK} 0%, ${PL_GREEN} 100%)`
                                            : '#d1d5db',
                                }}
                            >
                                {loading ? (
                                    <>
                                        <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                        Création du compte…
                                    </>
                                ) : (
                                    <>
                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                        Créer mon compte Pennylane
                                    </>
                                )}
                            </button>
                            {!allGood && (
                                <p className="text-xs text-red-500">Complétez d'abord les données manquantes ↓</p>
                            )}
                            {hasError && (
                                <p className="text-xs text-red-500">Une erreur s'est produite. Réessayez ou contactez le support.</p>
                            )}
                        </div>
                    )}

                    {/* Step 3 : autoriser OAuth */}
                    {isPendingOAuth && (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4">
                            <div className="flex items-start gap-3">
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-400">
                                    <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                    </svg>
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm font-semibold text-amber-800">
                                        Dernière étape — Autorisez Toola sur Pennylane
                                    </p>
                                    <p className="mt-1 text-xs text-amber-700">
                                        Vous allez être redirigé vers Pennylane pour accorder l'accès. Connectez-vous avec l'email <strong>{user?.billingEmail || user?.email}</strong> si besoin.
                                    </p>
                                    <button
                                        onClick={openOAuthWindow}
                                        disabled={!authorizeUrl}
                                        className="mt-3 flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:opacity-90 active:scale-95 disabled:opacity-50"
                                        style={{ background: `linear-gradient(135deg, ${PL_DARK} 0%, ${PL_GREEN} 100%)` }}
                                    >
                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                        Donner l'accès à Toola
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 4 : connecté */}
                    {isConnected && (
                        <div className="flex items-center gap-3 rounded-xl bg-green-50 border border-green-200 px-4 py-3">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-500">
                                <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-green-800">Tout est en ordre !</p>
                                <p className="text-xs text-green-600 mt-0.5">
                                    ID société Pennylane :{' '}
                                    <code className="font-mono">{user?.pennylaneCompanyId}</code>
                                </p>
                                {tokenInfo?.refreshTokenExpiresAt && (() => {
                                    const expiresAt = new Date(tokenInfo.refreshTokenExpiresAt);
                                    const daysLeft = Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                                    const dateStr = expiresAt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
                                    return daysLeft > 0 ? (
                                        <p className={`text-xs mt-1 font-medium ${daysLeft <= 30 ? 'text-amber-600' : 'text-green-600'}`}>
                                            {daysLeft <= 30 ? '⚠ ' : ''}Accès valide jusqu'au{' '}
                                            <strong>{dateStr}</strong>{' '}({daysLeft} jour{daysLeft > 1 ? 's' : ''})
                                        </p>
                                    ) : (
                                        <p className="text-xs mt-1 font-medium text-red-600">
                                            ⚠ Accès expiré depuis le {dateStr} — veuillez reconnecter Pennylane
                                        </p>
                                    );
                                })()}
                                {tokenInfo && !tokenInfo.refreshTokenExpiresAt && tokenInfo.accessTokenExpiresAt && (() => {
                                    const expiresAt = new Date(tokenInfo.accessTokenExpiresAt);
                                    const daysLeft = Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                                    const dateStr = expiresAt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
                                    return daysLeft > 0 ? (
                                        <p className={`text-xs mt-1 font-medium ${daysLeft <= 7 ? 'text-amber-600' : 'text-green-600'}`}>
                                            {daysLeft <= 7 ? '⚠ ' : ''}Token d'accès valide jusqu'au{' '}
                                            <strong>{dateStr}</strong>
                                        </p>
                                    ) : (
                                        <p className="text-xs mt-1 font-medium text-red-600">
                                            ⚠ Token expiré — veuillez reconnecter Pennylane
                                        </p>
                                    );
                                })()}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};

export default PennylaneOnboarding;
